import React, { useState, useEffect, useContext } from 'react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { AuthContext } from '../../contexts/auth-context'
import { toast } from 'sonner'
import { createWalletNotification, getMyWalletNotifications, sendBusinessOtp } from '../../services'
import GenericTable from '../../components/generic-data-table/GenericTable'
import { useNavigate } from 'react-router-dom'
import OtpVerifyModal from '../../components/modals/otp-modal/OtpVerifyModal'
import OtpVerification from '../../components/otp-verification/OtpVerification'

// SVG Icon for the Send Button
const SendIcon = (props) => (
  <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m22 2-7 20-4-9-9-4Z" />
    <path d="m22 2-11 11" />
  </svg>
)

function BusinessMessage() {
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState('all') // 'all' or 'segment'
  const [batteryLevel, setBatteryLevel] = useState(85)
  const [charging, setCharging] = useState(false)
  const [messageHistory, setMessageHistory] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [requireOtpVerification, setRequireOtpVerification] = useState(false)

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  })
  const navigate = useNavigate()

  const { auth, setOtp } = useContext(AuthContext)

  // Simulate battery level changes for mockup
  useEffect(() => {
    const batteryInterval = setInterval(() => {
      setBatteryLevel((prevLevel) => {
        if (prevLevel <= 20) setCharging(true)
        if (prevLevel >= 95) setCharging(false)
        return charging ? Math.min(100, prevLevel + 1) : Math.max(15, prevLevel - 1)
      })
    }, 8000)
    return () => clearInterval(batteryInterval)
  }, [charging])

  // Load message history on page change
  useEffect(() => {
    loadMessageHistory()
  }, [pagination.page])

  const loadMessageHistory = async () => {
    setIsLoading(true)
    try {
      const res = await getMyWalletNotifications({
        page: pagination.page,
        limit: pagination.limit,
      })

      const notifications = res?.data?.notifications || []

      // Map API response to table row format
      const mappedData = notifications.map((item) => ({
        id: item._id,
        date: new Date(item.createdAt).toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }),
        message: item.messageBody,
        segment: item.segment || 'All Customers',
        reach: item.reach ?? 0,
        status: item.status || 'pending',
        activeLoyaltyCard: item.activeLoyaltyCard || null, // keep the loyalty card info if present
      }))

      setMessageHistory(mappedData)

      setPagination((prev) => ({
        ...prev,
        total: res?.data?.total || 0,
        totalPages: res?.data?.totalPages || 0,
      }))
    } catch (error) {
      console.error('Error loading message history:', error)
      toast.error('Failed to load message history')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSendMessage = async () => {
    if (!message.trim()) {
      toast.error('Please enter a message before sending.')
      return
    }

    const payload = {
      userLoyaltyCardId: null,
      messageBody: message,
    }

    try {
      const loadingToast = toast.loading('Sending message...')

      const response = await createWalletNotification(payload)

      toast.dismiss(loadingToast)
      toast.success('Message sent successfully!')

      setMessage('')
      loadMessageHistory()
    } catch (err) {
      toast.dismiss()

      // Handle OTP verification requirement
      if (err?.response?.status === 403 && err?.response?.data?.needVerification) {
        toast.warning('OTP verification required')

        try {
          await sendBusinessOtp()
          setRequireOtpVerification(true)
        } catch (otpError) {
          console.error('Failed to send OTP:', otpError)
          toast.error('Failed to send OTP. Please try again.')
        }
        return
      }

      // Handle other errors
      const errorMessage = err?.response?.data?.message || 'Failed to send message'
      toast.error(errorMessage)
    }
  }

  const handlePageChange = (newPage) => setPagination((prev) => ({ ...prev, page: newPage }))

  // Helper for dynamic battery color in mockup
  const getBatteryColor = () => {
    if (batteryLevel > 50) return 'bg-green-500'
    if (batteryLevel > 20) return 'bg-yellow-500'
    return 'bg-red-500'
  }

  const currentTime = new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  const currentDayMonth = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  })

  // Message idea templates
  const messageIdeas = [
    "Flash sale! 30% off everything today only. Don't miss out!",
    'Your loyalty points expire soon. Use them before month-end!',
    'New arrivals are here! Be the first to check them out.',
    'Thank you for being a valued customer. Enjoy 15% off!',
  ]

  // Tailwind badge styles for different columns
  const badgeStyles = {
    status: {
      pending: 'bg-gradient-to-r from-amber-300 to-amber-500 text-white', // warm amber
      sent: 'bg-gradient-to-r from-emerald-400 to-emerald-600 text-white', // classy green
      failed: 'bg-gradient-to-r from-rose-400 to-rose-600 text-white', // muted red
      cancelled: 'bg-gradient-to-r from-gray-400 to-gray-600 text-white', // neutral gray
    },
    reach: 'bg-gradient-to-r from-sky-400 to-sky-600 text-white', // soft blue
    segment: 'bg-gradient-to-r from-indigo-400 to-indigo-600 text-white', // indigo purple
    date: 'bg-gradient-to-r from-slate-400 to-slate-600 text-white', // slate gray-blue
  }

  // Table columns configuration
  const columns = [
    {
      accessorKey: 'date',
      header: 'Date & Time',
      cell: ({ getValue }) => (
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${badgeStyles.date}`}>
          {getValue()}
        </span>
      ),
    },
    {
      accessorKey: 'message',
      header: 'Message',
      cell: ({ getValue }) => (
        <span className="px-3 py-1 rounded-lg bg-gradient-to-r from-gray-200 to-gray-300 text-gray-900 font-medium">
          {getValue()}
        </span>
      ),
    },
    {
      accessorKey: 'segment',
      header: 'Audience',
      cell: ({ row, getValue }) => {
        const loyaltyCardId = row.original?.activeLoyaltyCard?._id
        const value = getValue()

        if (loyaltyCardId) {
          return (
            <button
              onClick={() => navigate(`/business/my-customers/card/${loyaltyCardId}`)}
              className={`px-3 py-1 rounded-full text-sm font-medium ${badgeStyles.segment} hover:opacity-90 transition cursor-pointer`}
            >
              {value}
            </button>
          )
        }

        return (
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${badgeStyles.segment}`}>
            {value}
          </span>
        )
      },
    },
    {
      accessorKey: 'reach',
      header: 'Reach',
      cell: ({ getValue }) => (
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${badgeStyles.reach}`}>
          {getValue()}
        </span>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => {
        const status = getValue()?.toLowerCase()
        return (
          <span
            className={`px-3 py-1 rounded-full text-sm font-medium ${
              badgeStyles.status[status] || 'bg-gray-300 text-gray-800'
            }`}
          >
            {status}
          </span>
        )
      },
    },
  ]

  return (
    <div className="bg-slate-50 min-h-screen w-full font-sans">
      <OtpVerifyModal
        isOpen={requireOtpVerification}
        onClose={() => {
          setRequireOtpVerification(false)
          setOtp(['', '', '', ''])
        }}
      >
        <OtpVerification
          isMessageVerify
          onClose={() => {
            setRequireOtpVerification(false)
            setOtp(['', '', '', ''])
          }}
          handleSendMessage={handleSendMessage}
        />
      </OtpVerifyModal>
      <div className="container mx-auto p-4 sm:p-6 lg:p-8">
        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-5 lg:gap-8">
          {/* Left Section: Message Composer */}
          <div className="lg:col-span-3 bg-white rounded-xl shadow-lg border border-slate-200 p-8 mb-8 lg:mb-0">
            <div className="space-y-8">
              <div>
                <h1 className="text-3xl font-bold tracking-tight text-slate-900">
                  Send a Push Message
                </h1>
                <p className="text-slate-600 mt-2 text-base">
                  Reach your customers directly on their device's lock screen.
                </p>
              </div>

              {/* Audience Selection */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-800">1. Choose Audience</h3>
                <div className="flex items-center space-x-2 rounded-lg bg-slate-100 p-1">
                  <button
                    onClick={() => setMessageType('all')}
                    className={`w-1/2 rounded-md px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
                      messageType === 'all'
                        ? 'bg-white text-indigo-600 shadow-sm'
                        : 'text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    All Customers
                  </button>
                  {/* <button
                    // setMessageType('segment') --> replace this when add customer segment message
                    onClick={() => toast.info('Feature coming soon...')}
                    className={`w-full rounded-md px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
                      messageType === 'segment'
                        ? 'bg-white text-indigo-600 shadow-sm'
                        : 'text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    Customer Segment
                  </button> */}
                </div>
              </div>
              {/* Message Input */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-800">2. Compose Message</h3>
                <div className="relative">
                  <Textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="e.g. Happy Hour starts now! 50% off all drinks."
                    className="w-full min-h-[140px] resize-y rounded-lg border-slate-300 p-4 text-base focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    maxLength={160}
                  />
                  <p className="absolute bottom-3 right-4 text-sm text-slate-500">
                    {message.length} / 160
                  </p>
                </div>
              </div>

              {/* Message Ideas */}
              <div className="space-y-3">
                <h3 className="font-semibold text-slate-800">Need an idea?</h3>
                <div className="flex flex-wrap gap-2">
                  {messageIdeas.map((idea, index) => (
                    <button
                      key={index}
                      onClick={() => setMessage(idea)}
                      className="px-3 py-1.5 bg-slate-100 text-slate-700 text-sm rounded-full hover:bg-indigo-100 hover:text-indigo-700 transition-colors duration-200"
                    >
                      {idea.split('!')[0]}!
                    </button>
                  ))}
                </div>
              </div>

              {/* Send Button */}
              <div className="flex justify-end pt-4">
                <Button
                  onClick={handleSendMessage}
                  className="bg-theme-color-700 hover:bg-theme-color-800 text-white font-semibold text-base py-3 px-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-2 disabled:bg-slate-300 disabled:cursor-not-allowed"
                  disabled={!message.trim()}
                >
                  <SendIcon className="h-5 w-5" />
                  Send Message
                </Button>
              </div>
            </div>
          </div>

          {/* Right Section: Ultra-Realistic iPhone Display */}
          <div className="lg:col-span-2 flex justify-center items-start">
            <div className="relative mx-auto border-gray-800 dark:border-gray-800 bg-gray-800 border-[10px] rounded-[2.5rem] h-[630px] w-[315px] shadow-2xl">
              {/* Side Buttons */}
              <div className="h-[46px] w-[3px] bg-gray-800 absolute -start-[13px] top-[124px] rounded-s-lg"></div>
              <div className="h-[46px] w-[3px] bg-gray-800 absolute -start-[13px] top-[178px] rounded-s-lg"></div>
              <div className="h-[64px] w-[3px] bg-gray-800 absolute -end-[13px] top-[142px] rounded-e-lg"></div>

              <div className="rounded-[2rem] overflow-hidden w-full h-full bg-black">
                {/* Wallpaper and Lock Screen */}
                <div
                  className="relative w-full h-full bg-cover bg-center"
                  style={{
                    backgroundImage:
                      "url('https://images.unsplash.com/photo-1584968153982-a27915360912?q=80&w=1887&auto=format&fit=crop')",
                  }}
                >
                  {/* Status Bar */}
                  <div className="absolute top-0 left-0 right-0 h-11 px-2 flex justify-between items-center z-10">
                    <span className="text-white text-sm font-semibold w-16 text-center">
                      {currentTime.split(' ')[0]}
                    </span>
                    {/* Dynamic Island */}
                    <div className="absolute top-2.5 left-1/2 -translate-x-1/2 w-28 h-7 bg-black rounded-full"></div>
                    <div className="flex items-center space-x-1.5 w-16 justify-end">
                      {/* Cellular Icon */}
                      <svg width="17" height="17" viewBox="0 0 24 24" className="text-white">
                        <path
                          fill="currentColor"
                          d="M21 4H16V20H21V4M14 9H9V20H14V9M7 14H2V20H7V14Z"
                        />
                      </svg>
                      {/* Wifi Icon */}
                      <svg width="17" height="17" viewBox="0 0 24 24" className="text-white">
                        <path
                          fill="currentColor"
                          d="M12 20c.55 0 1-.45 1-1s-.45-1-1-1c-2.76 0-5-2.24-5-5c0-.55-.45-1-1-1s-1 .45-1 1c0 3.86 3.14 7 7 7zm0-12C8.14 8 5 11.14 5 15c0 .55-.45 1-1 1s-1-.45-1-1C3 10.03 7.03 6 12 6s9 4.03 9 9c0 .55-.45 1-1 1s-1-.45-1-1c0-3.86-3.14-7-7-7zm0 6c-1.65 0-3 1.35-3 3c0 .55-.45 1-1 1s-1-.45-1-1c0-2.76 2.24-5 5-5s5 2.24 5 5c0 .55-.45 1-1 1s-1-.45-1-1c0-1.65-1.35-3-3-3z"
                        />
                      </svg>
                      {/* Battery Icon */}
                      <div className="flex items-center text-white">
                        <div className="relative w-6 h-3 border border-white/80 rounded-sm">
                          <div
                            className={`h-full rounded-sm transition-all duration-300 ${getBatteryColor()}`}
                            style={{ width: `${Math.max(batteryLevel, 0)}%` }}
                          ></div>
                          {charging && (
                            <div className="absolute -inset-1 flex items-center justify-center">
                              <span className="text-green-400 text-[10px]">⚡</span>
                            </div>
                          )}
                        </div>
                        <div className="w-0.5 h-1 bg-white/80 rounded-r-sm -translate-x-px"></div>
                      </div>
                    </div>
                  </div>

                  {/* Lock Screen Time & Date */}
                  <div className="absolute top-24 left-0 right-0 text-center text-white px-6">
                    <div className="text-7xl font-light tracking-tight">{currentTime}</div>
                    <div className="text-lg font-medium opacity-90 tracking-wide">
                      {currentDayMonth}
                    </div>
                  </div>

                  {/* iOS Notification */}
                  <div className="absolute top-[280px] left-3 right-3">
                    <div className="bg-gray-900/20 backdrop-blur-xl rounded-3xl p-3 shadow-2xl border border-white/20">
                      <div className="flex items-start space-x-3">
                        {/* App Icon */}
                        <div className="w-10 h-10 flex-shrink-0 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center shadow-md">
                          {auth?.user?.profile_image ? (
                            <img
                              src={`${import.meta.env.VITE_BASE_URL}${auth?.user?.profile_image}`}
                              alt="Profile"
                              className="w-full h-full rounded-lg object-cover"
                            />
                          ) : (
                            <span className="text-white text-xl font-bold">
                              {auth?.user?.business_name?.[0]?.toUpperCase() || 'B'}
                            </span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="text-sm font-semibold text-white/90">
                              {auth?.user?.business_name || 'Your Business'}
                            </span>
                            <span className="text-xs text-white/60">now</span>
                          </div>
                          <p className="text-sm text-white break-words font-medium">
                            {message || 'Your message will appear here...'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Lock Screen Bottom Elements */}
                  <div className="absolute bottom-8 left-0 right-0 flex justify-between items-center px-12">
                    {/* Flashlight */}
                    <div className="w-12 h-12 bg-black/30 backdrop-blur-md rounded-full flex items-center justify-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6 text-white/80"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path d="M11 3a1 1 0 100 2h6a1 1 0 100-2h-6zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM4 11a1 1 0 011-1h6a1 1 0 110 2H5a1 1 0 01-1-1zM4 15a1 1 0 011-1h2a1 1 0 110 2H5a1 1 0 01-1-1z" />
                      </svg>
                    </div>
                    {/* Camera */}
                    <div className="w-12 h-12 bg-black/30 backdrop-blur-md rounded-full flex items-center justify-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6 text-white/80"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-1.121-1.121A2 2 0 0011.172 3H8.828a2 2 0 00-1.414.586L6.293 4.707A1 1 0 015.586 5H4zm6 9a3 3 0 100-6 3 3 0 000 6z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>

                  {/* Home Indicator Bar */}
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-36 h-1.5 bg-white/90 rounded-full"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Message History Table */}
        <div className="mt-12">
          <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-8">
            <div className="mb-6">
              <h2 className="text-2xl font-bold tracking-tight text-slate-900">Message History</h2>
              <p className="text-slate-600 mt-1">
                View and track all messages sent to your customers.
              </p>
            </div>
            <GenericTable
              columns={columns}
              data={messageHistory}
              pagination={pagination}
              onPageChange={handlePageChange}
              isLoading={isLoading}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default BusinessMessage
